import { Link } from 'react-router-dom';
import { MapPin, Mail, Phone, CheckCircle } from 'lucide-react';

const AboutEntity = () => {
  const services = [
    { name: 'Social Media Management', link: '/services/social-media' },
    { name: 'Video Production', link: '/services/video-production' },
    { name: 'Content Strategy', link: '/services/content-strategy' },
  ];

  const industries = [
    'Med Spas & Aesthetics Clinics',
    'Dental Practices',
    'Financial Advisors',
    'Real Estate Professionals',
    'Surgeons & Clinics',
    'Corporate & Small Businesses',
  ];

  return (
    <section className="py-16 sm:py-24 bg-background" aria-label="About Vantage Point Media">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10 sm:mb-14">
            <span className="inline-block text-accent font-semibold text-sm uppercase tracking-wider mb-3">
              About Us
            </span>
            <h2 className="font-display font-bold text-2xl sm:text-4xl lg:text-5xl text-foreground mb-4 sm:mb-6">
              About Vantage Point Media
            </h2>
          </div>

          {/* Entity Clarity Block */}
          <div className="bg-card border border-border rounded-2xl p-6 sm:p-10 shadow-soft mb-8 sm:mb-12">
            <p className="text-base sm:text-lg text-foreground leading-relaxed mb-6">
              <strong>Vantage Point Media</strong> is a video production and social media agency serving the Columbus, Ohio area. We help med spas, dentists, financial advisors, real estate professionals, surgeons, and small businesses grow their brands through cinematic video content, strategic social media management, and data-driven content strategy.
            </p>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-6">
              Businesses are increasingly investing in professional video and social media to stand out in competitive markets. Vantage Point Media provides a high-touch, boutique alternative to large agencies—offering dedicated teams, transparent pricing, and measurable results that drive revenue growth.
            </p>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              According to industry trends, short-form video content generates up to 3x more engagement than static posts, and businesses with consistent social media presence see 150–400% increases in profile activity within 90 days. Vantage Point Media leverages these trends to help clients attract high-value clients and generate leads consistently.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
            {/* Services */}
            <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-soft">
              <h3 className="font-display font-bold text-lg sm:text-xl text-foreground mb-4">Our Core Services</h3>
              <ul className="space-y-3">
                {services.map((service) => (
                  <li key={service.name}>
                    <Link 
                      to={service.link} 
                      className="flex items-center gap-3 text-foreground hover:text-primary transition-colors group"
                    >
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                      <span className="text-sm sm:text-base font-medium group-hover:underline">{service.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Industries */}
            <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-soft">
              <h3 className="font-display font-bold text-lg sm:text-xl text-foreground mb-4">Industries We Serve</h3>
              <ul className="space-y-3">
                {industries.map((industry) => (
                  <li key={industry} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                    <span className="text-muted-foreground text-sm sm:text-base">{industry}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Contact Info */}
          <div className="mt-8 bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-soft">
            <h3 className="font-display font-bold text-lg sm:text-xl text-foreground mb-4">Contact Vantage Point Media</h3>
            <div className="flex flex-wrap gap-6 sm:gap-8">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-primary" />
                <span className="text-muted-foreground text-sm sm:text-base">Columbus, Ohio Area</span>
              </div>
              <a href="mailto:shay@vantagepointmediagroup.com" className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors text-sm sm:text-base">
                <Mail className="w-4 h-4 text-primary" />
                shay@vantagepointmediagroup.com
              </a>
              <a href="tel:+16146250865" className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors text-sm sm:text-base">
                <Phone className="w-4 h-4 text-primary" />
                (614) 625-0865
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutEntity;
